import React, { useEffect, useRef, useState } from 'react';

import { Layout, PillButton, Text } from 'ds';
import { formatUsd, suggestedConsumablesMaxCapCentsFromProposed } from 'helpers';
import { wrapRequest } from 'helpers/requests';
import { verifyConsumablesOrderBudget } from 'ux/Tenant/PantryRestocking/requests';
import { updateCustomerAdminConsumablesOrderConfig } from 'ux/Tenant/TenantAdmin/Integrations/requests';
import { useAppSelector } from 'store/hooks';
import { selectHasTenantAdminType, selectTenancyId } from 'store/v2/User/selectors';

import type { ConsumablesPickerConfirmedApplyMode } from './AIAssistantConsumablesPickerConfirmedTypes';

type VerifyState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      valid: boolean;
      noBudgetConfigured: boolean;
      overBudget: boolean;
      proposedTotalCents: number | null;
      maxAllowedCents: number | null;
    };

export interface AIAssistantConsumablesPickerConfirmedAdminBudgetProps {
  orderId: number | null;
  applyMode: ConsumablesPickerConfirmedApplyMode;
}

const AIAssistantConsumablesPickerConfirmedAdminBudget: React.FC<
  AIAssistantConsumablesPickerConfirmedAdminBudgetProps
> = ({ orderId, applyMode }) => {
  const isTenantAdmin = useAppSelector(selectHasTenantAdminType);
  const tenancyId = useAppSelector(selectTenancyId);
  const [verifyState, setVerifyState] = useState<VerifyState>({ status: 'idle' });
  const [budgetCapUpdating, setBudgetCapUpdating] = useState(false);
  const [budgetVerifyRefreshKey, setBudgetVerifyRefreshKey] = useState(0);
  const budgetCapUpdateInFlightRef = useRef(false);

  useEffect(() => {
    if (!isTenantAdmin || orderId == null) {
      setVerifyState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    setVerifyState({ status: 'loading' });

    verifyConsumablesOrderBudget(orderId, [], applyMode)
      .then(response => {
        if (cancelled) return;
        const data = response.data;
        setVerifyState({
          status: 'ready',
          valid: Boolean(data.valid),
          noBudgetConfigured: Boolean(data.no_budget_configured),
          overBudget: Boolean(data.over_budget),
          proposedTotalCents:
            data.proposed_total != null && Number.isFinite(data.proposed_total) ? data.proposed_total : null,
          maxAllowedCents: data.max_allowed != null && Number.isFinite(data.max_allowed) ? data.max_allowed : null
        });
      })
      .catch(() => {
        if (cancelled) return;
        setVerifyState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [applyMode, isTenantAdmin, orderId, budgetVerifyRefreshKey]);

  if (!isTenantAdmin || orderId == null) {
    return null;
  }

  if (verifyState.status === 'idle' || verifyState.status === 'loading') {
    return (
      <Layout
        __className="AIAssistantConsumablesPickerConfirmed-adminBudget"
        direction="column"
        width="100%"
        minWidth={0}
      >
        <Text size="body-xs" color="gray-600">
          Checking order budget…
        </Text>
      </Layout>
    );
  }

  if (verifyState.status === 'error') {
    return (
      <Layout
        __className="AIAssistantConsumablesPickerConfirmed-adminBudget"
        direction="column"
        width="100%"
        minWidth={0}
      >
        <Text size="body-xs" color="gray-600">
          Could not verify order budget.
        </Text>
      </Layout>
    );
  }

  const { noBudgetConfigured, overBudget, proposedTotalCents, maxAllowedCents, valid } = verifyState;

  if (noBudgetConfigured) {
    return (
      <Layout
        __className="AIAssistantConsumablesPickerConfirmed-adminBudget"
        direction="column"
        gap={8}
        width="100%"
        minWidth={0}
      >
        <Text size="body-xs" color="gray-700">
          Your team has not set a max consumables order amount. Set a cap in pantry settings so non-admins can stay
          within budget when ordering.
        </Text>
      </Layout>
    );
  }

  const hasComparableAmounts = proposedTotalCents != null && maxAllowedCents != null && maxAllowedCents > 0;
  const showOverCopy = !valid && overBudget && hasComparableAmounts;
  const newCapCents =
    proposedTotalCents != null ? suggestedConsumablesMaxCapCentsFromProposed(proposedTotalCents) : null;

  const handleUpdateConsumablesMaxCap = async () => {
    if (!showOverCopy || tenancyId == null || newCapCents == null) return;
    if (budgetCapUpdateInFlightRef.current) return;
    budgetCapUpdateInFlightRef.current = true;
    setBudgetCapUpdating(true);
    try {
      await wrapRequest(updateCustomerAdminConsumablesOrderConfig(tenancyId, { max_amount_cents: newCapCents }), {
        showSuccessMessage: false
      });
      setBudgetVerifyRefreshKey(k => k + 1);
    } catch {
      // wrapRequest already showed the error snackbar
    } finally {
      budgetCapUpdateInFlightRef.current = false;
      setBudgetCapUpdating(false);
    }
  };

  if (valid && hasComparableAmounts && proposedTotalCents != null && maxAllowedCents != null) {
    return (
      <Layout
        __className="AIAssistantConsumablesPickerConfirmed-adminBudget"
        direction="column"
        width="100%"
        minWidth={0}
      >
        <Text size="body-xs" color="gray-700">
          {applyMode === 'recurring'
            ? `Your team's recurring pantry estimate for the next open order is ${formatUsd(
                proposedTotalCents / 100
              )} (max order budget ${formatUsd(maxAllowedCents / 100)}).`
            : `The next open order is estimated at ${formatUsd(proposedTotalCents / 100)} (max order budget ${formatUsd(
                maxAllowedCents / 100
              )}).`}
        </Text>
      </Layout>
    );
  }

  if (showOverCopy && proposedTotalCents != null && maxAllowedCents != null && newCapCents != null) {
    return (
      <Layout
        __className="AIAssistantConsumablesPickerConfirmed-adminBudget"
        direction="column"
        gap={8}
        width="100%"
        minWidth={0}
      >
        <Text size="body-xs" color="red-900">
          {applyMode === 'recurring'
            ? `Your team's recurring pantry estimate for the next open order is above the max order budget of ${formatUsd(
                maxAllowedCents / 100
              )}. That cap applies to non-admins. Update the cap to ${formatUsd(newCapCents / 100)}?`
            : `The next open order is estimated above the max order budget of ${formatUsd(
                maxAllowedCents / 100
              )}. That cap applies to non-admins. Update the cap to ${formatUsd(newCapCents / 100)}?`}
        </Text>
        <Layout direction="row" align="center" gap={8}>
          <PillButton
            type="primary"
            size="sm"
            text="Update"
            disabled={tenancyId == null || budgetCapUpdating}
            showSpinner={budgetCapUpdating}
            onClick={() => {
              void handleUpdateConsumablesMaxCap();
            }}
          />
          {tenancyId == null ? (
            <Text size="body-xs" color="gray-600">
              Unable to update budget from this session.
            </Text>
          ) : null}
        </Layout>
      </Layout>
    );
  }

  return null;
};

export default AIAssistantConsumablesPickerConfirmedAdminBudget;
